import { z } from "zod/v4";

import {
  RuleGraphDtoDescriptions,
  RuleGraphEdgeDescriptions,
  RuleGraphNodeDescriptions,
  RuleGraphNodeMetaDescriptions,
} from "../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../descriptions/types";
import type { AgenticLogicOrgExclusionsDto } from "./exclusions";

/**
 * Rule graph artifact — the renderer-agnostic node/edge description of a
 * rule's selection logic, stored alongside the code (never geometry: layout
 * is client-side).
 *
 * Identity contract: a `branch` node's id equals the rule code's
 * `attempt()` BASE label (always a static string literal). Runtime attempt
 * instances (`attempt('by-tag', { instance: tag })` in a loop) are drill-in
 * data on that one node, never nodes of their own — that is what keeps a
 * looped ladder from exploding the graph, and what lets the
 * graph_consistency gate assert code ≡ graph statically.
 *
 * The caps are the anti-hairball contract: correct aggregation (one
 * generator node with `meta.itemCount` for a 1000-product list) stays tiny,
 * while per-item emission blows past the cap immediately. Enforced once in
 * the Zod schema; every consumer re-enforces it for free via parse.
 *
 * The arithmetic the node cap is sized against: a rule row is its own full
 * pipeline — branch + generator + up to two post-filters + sort + limit, so
 * SIX nodes per row — plus the input, output and fallback terminal. 150
 * therefore admits a ~24-branch chain (a migrated legacy block of that many
 * rules) while a per-item list of even 200 products still blows the cap on
 * its first row.
 */
export const RULE_GRAPH_VERSION = 1;
export const RULE_GRAPH_MAX_NODES = 150;
export const RULE_GRAPH_MAX_EDGES = 400;
export const RULE_GRAPH_EXCLUSIONS_NODE_ID = "__ig_platform_exclusions__";

export const RuleGraphNodeKindList = [
  "input",
  "branch",
  "generator",
  "filter",
  "sort",
  "limit",
  "exclusions",
  "output",
  "fallback-terminal",
] as const;

export const RuleGraphNodeKindSchema = z.enum(RuleGraphNodeKindList);
export type RuleGraphNodeKind = (typeof RuleGraphNodeKindList)[number];

/**
 * The kinds that describe a selection STEP, as opposed to the row header
 * (`branch`) and the three anchors (`input`, `output`, `fallback-terminal`).
 *
 * Rule-authored pipeline steps may carry a `branchId`: the field names the row
 * a step runs inside, so a header naming itself, or an anchor naming any row,
 * is a category error rather than a stylistic one. The platform Exclusions
 * step is also a pipeline step for layout and coverage, but is always shared
 * and therefore may not carry `branchId`.
 */
export const RuleGraphPipelineNodeKindList = [
  "generator",
  "filter",
  "sort",
  "limit",
  "exclusions",
] as const;

export type RuleGraphPipelineNodeKind =
  (typeof RuleGraphPipelineNodeKindList)[number];

export const isRuleGraphPipelineKind = (
  kind: RuleGraphNodeKind
): kind is RuleGraphPipelineNodeKind =>
  (RuleGraphPipelineNodeKindList as readonly string[]).includes(kind);

export const RuleGraphEdgeKindList = ["flow", "fallback"] as const;
export const RuleGraphEdgeKindSchema = z.enum(RuleGraphEdgeKindList);
export type RuleGraphEdgeKind = (typeof RuleGraphEdgeKindList)[number];

/**
 * Which request context a branch node's condition chip is about. Drives the
 * row-header sentence the canvas prints ahead of the chip ("When cart
 * contains …" vs "When delivery is …"). Absent means `cart` at render time —
 * a renderer default, never a schema default, so every stored graph stays
 * byte-identical.
 */
export const RuleGraphConditionSubjectList = [
  "cart",
  "delivery",
  "shipping",
  "customer",
  "page",
  "order",
  "time",
] as const;

export const RuleGraphConditionSubjectSchema = z.enum(
  RuleGraphConditionSubjectList
);

export type RuleGraphConditionSubject =
  (typeof RuleGraphConditionSubjectList)[number];

/**
 * Typed node meta. Loose on purpose: unknown keys emitted by older
 * generators (or future ones) ride through untouched, while the keys the
 * renderer understands are contract-checked. Aggregates belong here —
 * never one node per item.
 */
export const RuleGraphNodeMetaSchema = z
  .looseObject({
    itemCount: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(RuleGraphNodeMetaDescriptions.itemCount),
    sampleItems: z
      .array(z.string())
      .max(5)
      .optional()
      .describe(RuleGraphNodeMetaDescriptions.sampleItems),
    condition: z
      .string()
      .max(300)
      .optional()
      .describe(RuleGraphNodeMetaDescriptions.condition),
    conditionSubject: RuleGraphConditionSubjectSchema.optional().describe(
      RuleGraphNodeMetaDescriptions.conditionSubject
    ),
    sortKey: z
      .string()
      .max(120)
      .optional()
      .describe(RuleGraphNodeMetaDescriptions.sortKey),
    limit: z
      .number()
      .int()
      .positive()
      .optional()
      .describe(RuleGraphNodeMetaDescriptions.limit),
  })
  .describe(RuleGraphNodeMetaDescriptions._schema);

export type RuleGraphNodeMeta = z.infer<typeof RuleGraphNodeMetaSchema>;

export const RuleGraphNodeSchema = z
  .object({
    id: z.string().min(1).max(200).describe(RuleGraphNodeDescriptions.id),
    kind: RuleGraphNodeKindSchema.describe(RuleGraphNodeDescriptions.kind),
    label: z.string().min(1).max(200).describe(RuleGraphNodeDescriptions.label),
    summary: z
      .string()
      .max(500)
      .optional()
      .describe(RuleGraphNodeDescriptions.summary),
    branchId: z
      .string()
      .min(1)
      .max(200)
      .optional()
      .describe(RuleGraphNodeDescriptions.branchId),
    meta: RuleGraphNodeMetaSchema.optional().describe(
      RuleGraphNodeDescriptions.meta
    ),
  })
  .describe(RuleGraphNodeDescriptions._schema);

export type RuleGraphNode = z.infer<typeof RuleGraphNodeSchema>;

export const RuleGraphEdgeSchema = z
  .object({
    from: z.string().min(1).describe(RuleGraphEdgeDescriptions.from),
    to: z.string().min(1).describe(RuleGraphEdgeDescriptions.to),
    kind: RuleGraphEdgeKindSchema.describe(RuleGraphEdgeDescriptions.kind),
  })
  .describe(RuleGraphEdgeDescriptions._schema);

export type RuleGraphEdge = z.infer<typeof RuleGraphEdgeSchema>;

// Kept unrefined on purpose (Zod v4: .omit()/.extend() break on refined
// schemas). Cross-field invariants live in validateRuleGraphStructure below.
export const RuleGraphDtoSchema = z
  .object({
    version: z
      .literal(RULE_GRAPH_VERSION)
      .describe(RuleGraphDtoDescriptions.version),
    nodes: z
      .array(RuleGraphNodeSchema)
      .min(2)
      .max(RULE_GRAPH_MAX_NODES)
      .describe(RuleGraphDtoDescriptions.nodes),
    edges: z
      .array(RuleGraphEdgeSchema)
      .max(RULE_GRAPH_MAX_EDGES)
      .describe(RuleGraphDtoDescriptions.edges),
  })
  .describe(RuleGraphDtoDescriptions._schema);

export type RuleGraphDto = z.infer<typeof RuleGraphDtoSchema>;

/**
 * Adds the platform-owned exclusion step to a rule-authored graph.
 *
 * The model and template builders own selection logic only. Exclusions run
 * after that logic and before shopper output, so this function redirects every
 * flow edge into output through one shared step and gives that step its own
 * empty-result edge. It is intentionally idempotent because generated, stored,
 * template, draft and version graphs all pass through stamping boundaries.
 *
 * Invalid or colliding graphs are returned unchanged. Existing structural
 * validation then reports the original problem instead of receiving a partly
 * stamped topology.
 */
export function stampPlatformExclusionsOnGraph(
  graph: RuleGraphDto
): RuleGraphDto {
  if (graph.nodes.some((node) => node.kind === "exclusions")) {
    return graph;
  }
  if (graph.nodes.some((node) => node.id === RULE_GRAPH_EXCLUSIONS_NODE_ID)) {
    return graph;
  }

  const outputs = graph.nodes.filter((node) => node.kind === "output");
  const terminals = graph.nodes.filter(
    (node) => node.kind === "fallback-terminal"
  );
  if (outputs.length !== 1 || terminals.length !== 1) {
    return graph;
  }
  const output = outputs[0];
  const terminal = terminals[0];
  if (!output || !terminal) {
    return graph;
  }

  const incomingOutputFlows = graph.edges.filter(
    (edge) => edge.kind === "flow" && edge.to === output.id
  );
  if (incomingOutputFlows.length === 0) {
    return graph;
  }

  return {
    ...graph,
    nodes: [
      ...graph.nodes,
      {
        id: RULE_GRAPH_EXCLUSIONS_NODE_ID,
        kind: "exclusions",
        label: "Applied to every result",
        summary:
          "Org-wide product, tag, collection, and vendor exclusions are applied by the platform.",
      },
    ],
    edges: [
      ...graph.edges.map((edge) =>
        edge.kind === "flow" && edge.to === output.id
          ? { ...edge, to: RULE_GRAPH_EXCLUSIONS_NODE_ID }
          : edge
      ),
      {
        from: RULE_GRAPH_EXCLUSIONS_NODE_ID,
        to: output.id,
        kind: "flow",
      },
      {
        from: RULE_GRAPH_EXCLUSIONS_NODE_ID,
        to: terminal.id,
        kind: "fallback",
      },
    ],
  };
}

/**
 * The org-wide exclusions the Exclusions step applies. A structural subset of
 * the org-exclusions DTO so the SDK's rule-visible shape (which drops
 * `lastUpdateTs`) fits too.
 */
export type RuleGraphOrgExclusions = Pick<
  AgenticLogicOrgExclusionsDto,
  "productIds" | "tags" | "collections" | "vendors"
>;

const joinAsSentence = (parts: readonly string[]): string =>
  parts.length <= 1
    ? (parts[0] ?? "")
    : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;

const RULE_GRAPH_SUMMARY_MAX = 500;

/**
 * The Exclusions card's words, from the org's CURRENT exclusions.
 *
 * The step is stamped structurally at every producer with a placeholder,
 * because the producers (a model reading source, a template's `describeGraph`)
 * do not hold org state. The reader does: the canvas overlays this text at
 * render time so the card names what is actually excluded today, and follows
 * the Exclusions tab without a republish — exactly as serving does.
 */
export function exclusionsNodeText(exclusions: RuleGraphOrgExclusions): {
  label: string;
  summary: string;
} {
  const groups = [
    { noun: "product", values: exclusions.productIds, named: false },
    { noun: "tag", values: exclusions.tags, named: true },
    { noun: "collection", values: exclusions.collections, named: true },
    { noun: "vendor", values: exclusions.vendors, named: true },
  ].filter((group) => group.values.length > 0);

  if (groups.length === 0) {
    return {
      label: "No org-wide exclusions",
      summary:
        "No products, tags, collections, or vendors are excluded store-wide. Exclusions set on the Exclusions tab apply to every rule's results.",
    };
  }

  const counts = groups.map(
    ({ noun, values }) =>
      `${values.length} ${noun}${values.length === 1 ? "" : "s"}`
  );
  const details = groups.map(({ noun, values, named }) =>
    named
      ? `${noun}${values.length === 1 ? "" : "s"} ${values.join(", ")}`
      : `${values.length} ${noun}${values.length === 1 ? "" : "s"}`
  );
  const summary = `Excluded from every rule's results, store-wide: ${joinAsSentence(details)}. Edit them on the Exclusions tab.`;
  return {
    label: `Excludes ${joinAsSentence(counts)}`,
    summary:
      summary.length <= RULE_GRAPH_SUMMARY_MAX
        ? summary
        : `${summary.slice(0, RULE_GRAPH_SUMMARY_MAX - 1)}…`,
  };
}

/**
 * Rewrites the Exclusions step's copy from the org's current exclusions. A
 * graph without the step is returned as is.
 */
export function stampOrgExclusionsOnGraph(
  graph: RuleGraphDto,
  exclusions: RuleGraphOrgExclusions
): RuleGraphDto {
  if (!graph.nodes.some((node) => node.kind === "exclusions")) {
    return graph;
  }
  const text = exclusionsNodeText(exclusions);
  return {
    ...graph,
    nodes: graph.nodes.map((node) =>
      node.kind === "exclusions" ? { ...node, ...text } : node
    ),
  };
}

export const RuleGraphStructuralErrorCodeList = [
  "duplicate_node_id",
  "unresolved_edge_endpoint",
  "missing_input",
  "multiple_input",
  "missing_output",
  "multiple_output",
  "missing_fallback_terminal",
  "multiple_fallback_terminal",
  "unresolved_branch_id",
  "branch_id_on_non_pipeline_node",
  "branch_id_on_exclusions_node",
  "reserved_exclusions_node_id",
  "cycle",
  "invalid_fallback_target",
  "invalid_branch_entry_edge",
  "input_has_in_edges",
  "terminal_has_out_edges",
] as const;

export type RuleGraphStructuralErrorCode =
  (typeof RuleGraphStructuralErrorCodeList)[number];

export interface RuleGraphStructuralError {
  code: RuleGraphStructuralErrorCode;
  message: string;
  nodeId?: string;
}

/**
 * Cross-field invariants the (deliberately unrefined) schema cannot carry.
 * Pure and dependency-free so the publish gate, the draft-save path, and any
 * future consumer run the identical check.
 *
 * Returns every violation found (not fail-fast) so gate detailsJson can hand
 * an authoring agent the full self-heal list in one round.
 */
export function validateRuleGraphStructure(
  graph: RuleGraphDto
): RuleGraphStructuralError[] {
  const errors: RuleGraphStructuralError[] = [];
  const nodesById = new Map<string, RuleGraphNode>();

  for (const node of graph.nodes) {
    if (nodesById.has(node.id)) {
      errors.push({
        code: "duplicate_node_id",
        message: `Duplicate node id '${node.id}'.`,
        nodeId: node.id,
      });
      continue;
    }
    nodesById.set(node.id, node);
  }

  const countOf = (kind: RuleGraphNodeKind): number =>
    graph.nodes.filter((node) => node.kind === kind).length;
  const cardinality = (
    kind: RuleGraphNodeKind,
    missing: RuleGraphStructuralErrorCode,
    multiple: RuleGraphStructuralErrorCode
  ): void => {
    const count = countOf(kind);
    if (count === 0) {
      errors.push({
        code: missing,
        message: `Graph must contain exactly one '${kind}' node; found none.`,
      });
    } else if (count > 1) {
      errors.push({
        code: multiple,
        message: `Graph must contain exactly one '${kind}' node; found ${count}.`,
      });
    }
  };
  cardinality("input", "missing_input", "multiple_input");
  cardinality("output", "missing_output", "multiple_output");
  cardinality(
    "fallback-terminal",
    "missing_fallback_terminal",
    "multiple_fallback_terminal"
  );

  for (const node of graph.nodes) {
    if (
      node.id === RULE_GRAPH_EXCLUSIONS_NODE_ID &&
      node.kind !== "exclusions"
    ) {
      errors.push({
        code: "reserved_exclusions_node_id",
        message: `Node id '${RULE_GRAPH_EXCLUSIONS_NODE_ID}' is reserved for the platform Exclusions step.`,
        nodeId: node.id,
      });
    }
    if (node.branchId === undefined) {
      continue;
    }
    if (node.kind === "exclusions") {
      errors.push({
        code: "branch_id_on_exclusions_node",
        message: `The platform Exclusions node '${node.id}' must not set branchId; it runs after every rule-authored branch.`,
        nodeId: node.id,
      });
      continue;
    }
    // `branchId` says which ROW a step runs inside, so only a step may carry
    // it. A branch naming itself resolves perfectly well and still means
    // nothing — and the canvas, which reads the field to collect a row's
    // cards, then finds a member it can never place and abandons the whole
    // rule-row layout for the generic one. Caught here so the draft save and
    // the graph_consistency gate reject it with a self-heal message, rather
    // than shipping a graph that only misrenders.
    if (!isRuleGraphPipelineKind(node.kind)) {
      errors.push({
        code: "branch_id_on_non_pipeline_node",
        message: `Node '${node.id}' is a '${node.kind}' node and must not set branchId (it names '${node.branchId}'); only ${RuleGraphPipelineNodeKindList.join("/")} nodes belong to a branch.`,
        nodeId: node.id,
      });
      continue;
    }
    const owner = nodesById.get(node.branchId);
    if (!owner || owner.kind !== "branch") {
      errors.push({
        code: "unresolved_branch_id",
        message: `Node '${node.id}' names branchId '${node.branchId}', which is not a 'branch' node in this graph.`,
        nodeId: node.id,
      });
    }
  }

  let edgesResolve = true;
  for (const edge of graph.edges) {
    for (const endpoint of [edge.from, edge.to]) {
      if (!nodesById.has(endpoint)) {
        edgesResolve = false;
        errors.push({
          code: "unresolved_edge_endpoint",
          message: `Edge ${edge.from} → ${edge.to} references unknown node id '${endpoint}'.`,
          nodeId: endpoint,
        });
      }
    }
    const target = nodesById.get(edge.to);
    if (
      edge.kind === "fallback" &&
      target &&
      target.kind !== "branch" &&
      target.kind !== "fallback-terminal"
    ) {
      errors.push({
        code: "invalid_fallback_target",
        message: `Fallback edge ${edge.from} → ${edge.to} must terminate at a 'branch' or the 'fallback-terminal', not a '${target.kind}' node.`,
        nodeId: edge.to,
      });
    }
    const from = nodesById.get(edge.from);
    // Rule ROWS are chained by `fallback`, never by `flow`: a row runs
    // because the row above it came up EMPTY, not because it consumes that
    // row's results. A flow edge may therefore only ENTER the ladder — from
    // the input, or through the shared lead-in steps a rule runs once before
    // any row (`branchId`-less pipeline nodes). A step that belongs to a row,
    // or a row header itself, flowing into another header asserts an
    // execution order the runtime never has, and the canvas — which walks
    // fallback edges to build the rows — then finds no chain, abandons the
    // rule rows for the generic DAG pass, and draws every header on one long
    // horizontal strip. Caught here so the draft save and the
    // graph_consistency gate reject it with a self-heal message, rather than
    // shipping a graph that only misrenders.
    if (
      edge.kind === "flow" &&
      target?.kind === "branch" &&
      from &&
      from.kind !== "input" &&
      !(from.branchId === undefined && isRuleGraphPipelineKind(from.kind))
    ) {
      errors.push({
        code: "invalid_branch_entry_edge",
        message: `Flow edge ${edge.from} → ${edge.to} enters branch '${edge.to}' from a '${from.kind}' node that is not the input or a shared pre-branch step. Chain branches with 'fallback' edges instead.`,
        nodeId: edge.to,
      });
    }
    if (target?.kind === "input") {
      errors.push({
        code: "input_has_in_edges",
        message: `The 'input' node '${edge.to}' must have no incoming edges.`,
        nodeId: edge.to,
      });
    }
    if (from && (from.kind === "output" || from.kind === "fallback-terminal")) {
      errors.push({
        code: "terminal_has_out_edges",
        message: `Terminal node '${edge.from}' ('${from.kind}') must have no outgoing edges.`,
        nodeId: edge.from,
      });
    }
  }

  // Kahn's algorithm — only meaningful once every endpoint resolves.
  if (edgesResolve) {
    const inDegree = new Map<string, number>();
    const outEdges = new Map<string, string[]>();
    for (const node of graph.nodes) {
      inDegree.set(node.id, 0);
      outEdges.set(node.id, []);
    }
    for (const edge of graph.edges) {
      inDegree.set(edge.to, (inDegree.get(edge.to) ?? 0) + 1);
      outEdges.get(edge.from)?.push(edge.to);
    }
    const queue = graph.nodes
      .map((node) => node.id)
      .filter((id) => inDegree.get(id) === 0);
    let visited = 0;
    while (queue.length > 0) {
      const id = queue.shift() as string;
      visited += 1;
      for (const next of outEdges.get(id) ?? []) {
        const remaining = (inDegree.get(next) ?? 0) - 1;
        inDegree.set(next, remaining);
        if (remaining === 0) {
          queue.push(next);
        }
      }
    }
    if (visited < nodesById.size) {
      errors.push({
        code: "cycle",
        message: "Graph edges must form a DAG; a cycle was detected.",
      });
    }
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Compile-time checks: description objects must cover all schema keys
// ---------------------------------------------------------------------------

assertDescriptions<typeof RuleGraphNodeDescriptions, RuleGraphNode>();
assertDescriptions<typeof RuleGraphNodeMetaDescriptions, RuleGraphNodeMeta>();
assertDescriptions<typeof RuleGraphEdgeDescriptions, RuleGraphEdge>();
assertDescriptions<typeof RuleGraphDtoDescriptions, RuleGraphDto>();
